import React from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '@design-system/NavBar';
import { Skeleton } from '@design-system/Skeleton';
import { StandardContainer } from '@design-system/StandardContainer';
import { ConditionsCard } from '@design-system/Cards/ConditionCard';
import { cn } from '@/utils/styles';
import { EventTracker } from '@/utils';

interface Connection {
  id: number;
  name: string;
  rank: string;
  rank_color: string;
}

interface HealthCondition {
  title: string;
  tag: string;
  tag_color: string;
  description: string;
  no_of_connections: number;
  connections: Connection[];
}

interface HealthConditionContainerProps {
  condition: HealthCondition;
  isLoading: boolean;
}

const ConnectionsSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-6">
    {Array.from({ length: 4 }).map((_, index) => (
      <Skeleton
        key={`connection-skeleton-${index}`}
        className="h-[78px] w-full rounded-lg"
      />
    ))}
  </div>
);

const ConditionSkeletonView = () => (
  <div className="mt-12">
    <Skeleton className="h-8 w-[50%] rounded-md mb-6" />
    <Skeleton className="h-6 w-[30%] rounded-md mb-4" />
    <Skeleton className="h-20 w-[80%] rounded-md mb-6" />
    <ConnectionsSkeleton />
  </div>
);

export const HealthConditionContainer: React.FC<
  HealthConditionContainerProps
> = ({ condition, isLoading }) => {
  const navigate = useNavigate();

  const { title, tag, tag_color, description, no_of_connections, connections } =
    condition;

  const navigateToConditionsPage = (
    id: number,
    conditionName: string,
    scoreName: string,
  ) => {
    EventTracker.trackEvent(
      'health',
      `health_healthConnections_${conditionName.replace(/\s+/g, '_')}_${scoreName.replace(/\s+/g, '_')}`,
    );
    return navigate(`/health/connections/${encodeURIComponent(title)}/${id}`);
  };

  return (
    <div className="min-h-screen">
      <StandardContainer>
        <NavBar returnButtonTitle="Health" className="md:-ml-5 mt-8" />

        {isLoading ? (
          <ConditionSkeletonView />
        ) : (
          <div className="mt-12">
            {/* Title and Tag */}
            <div className="flex flex-col gap-4">
              <h1 className="dark typography-h1 text-primary">{title}</h1>
            </div>
            {tag && (
              <div
                className={cn(
                  'inline-flex items-center justify-center px-2 py-1 mt-4 rounded',
                )}
                style={{
                  backgroundColor: tag_color,
                }}
              >
                <p className="typography-caption1 text-primary">{tag}</p>
              </div>
            )}

            {/* Description */}
            <p className="typography-body text-secondary mt-6 max-w-[37rem]">
              {description}
            </p>

            {/* Connections */}
            {no_of_connections > 0 ? (
              <div className="mt-12">
                <h2 className="typography-h2 text-primary">
                  {`${no_of_connections} connection${
                    no_of_connections > 1 ? 's' : ''
                  }`}
                </h2>
                <p className="typography-body text-secondary mt-2 max-w-[37rem]">
                  We’ve found connections between the scores below and this
                  condition. Explore the supporting research and data behind
                  each link.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-6">
                  {connections.map((connection, index) => (
                    <ConditionsCard
                      key={`connection-${connection.name}-${index}-card`}
                      id={connection.id}
                      name={connection.name}
                      rank={connection.rank}
                      rankColor={connection.rank_color}
                      onClick={() =>
                        navigateToConditionsPage(
                          connection.id,
                          connection.name,
                          title,
                        )
                      }
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-12">
                <h2 className="typography-h2 text-primary">No connections</h2>
                <p className="typography-body text-secondary mt-4">
                  No connections were found between your Viome health scores and
                  this condition.
                </p>
              </div>
            )}
          </div>
        )}
      </StandardContainer>
    </div>
  );
};
